import React, { Component, PropsWithChildren, Fragment } from 'react';
import { createPortal } from 'react-dom';
import './BlockUi.scss';
import Overlay from './Overlay';
import { IBlockProps } from './types';

/**
 * BlockWindow hovers entire window and prevents focus and tabbing inside.
 * It restores focus back to previously focused element
 */
export class BlockWindow extends Component<PropsWithChildren<IBlockProps>, {}> {
  // blocking overlay
  private overlay: HTMLElement | null;

  // we will restore focus to this element when block hides
  private lastFocused: HTMLElement | null;

  // remember scrollY right before we change focus to prevent undesired scroll
  private scrollPosition: number;

  public componentDidMount() {
    window.document.addEventListener('keydown', this.onKeyDown);
    if (this.props.blocking) {
      this.focusOverlay();
    }
  }

  public componentWillUnmount() {
    window.document.removeEventListener('keydown', this.onKeyDown);
  }

  public componentDidUpdate(prevProps: IBlockProps) {
    if (this.props.blocking && !prevProps.blocking) {
      this.scrollPosition = window.pageYOffset;
      this.focusOverlay();
    }
    if (!this.props.blocking && prevProps.blocking) {
      if (this.lastFocused) {
        this.lastFocused.focus();
        window.scrollTo(0, this.scrollPosition);
      }
    }
  }

  public render() {
    const style = this.props.zIndex ? { zIndex: this.props.zIndex } : undefined;
    return (
      <Fragment>
        {this.props.blocking &&
          createPortal(
            <div
              className={'hlc-blockui-fullscreen hlc-blockui-busy'}
              aria-live={'polite'}
              aria-busy={true}
              style={style}
            >
              <Overlay setRef={this.setOverlayRef} />
            </div>,
            document.body,
          )}
        {this.props.children}
      </Fragment>
    );
  }

  private focusOverlay() {
    this.lastFocused = document.activeElement as HTMLElement;
    if (this.overlay) {
      this.overlay.focus();
      window.scrollTo(0, this.scrollPosition);
    }
  }

  private setOverlayRef = (ref: HTMLElement | null) => {
    this.overlay = ref;
  };

  // prevent tabbing inside
  private onKeyDown = (e: KeyboardEvent) => {
    if (!this.props.blocking || !this.overlay) {
      return;
    }
    if (e.keyCode === 9) {
      this.overlay.focus();
    }
  };
}

export default BlockWindow;
